/* eslint-disable react/display-name */
import { Button, Input, Space, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from 'src/styles/layout.module.css';
import DevicesTable from './DevicesTable';
import {
  addDeviceToGroup,
  fetchDeviceNotInGroup,
  fetchGroupDevices,
  removeDevicesFromGroup,
} from 'src/features/deviceGroups/deviceGroups.state';
import { AppDispatch } from 'src/index';
import { useDispatch } from 'react-redux';
import { useDeviceGroupDevicesById } from 'src/features/deviceGroups/deviceGroups.hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { GroupDevice } from 'src/features/deviceGroups/deviceGroups.types';
import { useDebounce } from 'react-use';
import { SearchOutlined } from '@ant-design/icons';

const DeviceGroupDevicesTab: React.FC = () => {
  const { groupId } = useParams() as { groupId: string };
  const dispatch: AppDispatch = useDispatch();
  const intl = useIntl();
  const devices = useDeviceGroupDevicesById(groupId);
  const [addMode, setAddMode] = useState(false);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const [newGroupDevice, setNewGroupDevice] = useState<GroupDevice[]>([]);

  useEffect(() => {
    dispatch(fetchGroupDevices({ groupId }));
  }, [dispatch, groupId]);

  const getDevicesNotInGroup = async () => {
    const res = await dispatch(fetchDeviceNotInGroup({ groupId, params: { search } }));
    if (fetchDeviceNotInGroup.fulfilled.match(res)) {
      setNewGroupDevice(res.payload);
    }
  };

  const onAddDevice = useCallback(
    async (device: GroupDevice) => {
      const res = await dispatch(addDeviceToGroup({ groupId, data: { deviceId: device.phyDeviceId } }));
      if (addDeviceToGroup.fulfilled.match(res)) {
        setNewGroupDevice(newGroupDevice.filter(f => f.id !== device.id));
        dispatch(fetchGroupDevices({ groupId }));
      }
    },
    [dispatch, groupId, newGroupDevice]
  );

  const onRemove = useCallback(
    async (device: GroupDevice) => {
      await dispatch(removeDevicesFromGroup({ groupId, deviceId: device.phyDeviceId }));
    },
    [dispatch, groupId]
  );

  useDebounce(
    () => {
      if (search !== undefined) {
        if (addMode) {
          (async () => await getDevicesNotInGroup())();
        } else {
          dispatch(fetchGroupDevices({ groupId, params: { search } }));
        }
      }
    },
    300,
    [dispatch, search]
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Space style={{ width: '100%', marginBottom: 10, justifyContent: 'space-between' }}>
          <Typography.Title level={4} style={{ marginBottom: 0 }}>
            Шлемы
          </Typography.Title>
          {addMode ? (
            <Button
              onClick={() => {
                setAddMode(false);
              }}
            >
              <FormattedMessage id={'action.close'} />
            </Button>
          ) : (
            <Button
              type={'primary'}
              onClick={() => {
                setAddMode(true);
                getDevicesNotInGroup();
              }}
            >
              <FormattedMessage id={'action.add'} />
            </Button>
          )}
        </Space>
      </div>
      <div className={styles.content}>
        <Input
          autoFocus
          allowClear
          placeholder={intl.formatMessage({ id: 'placeholder.search' })}
          prefix={<SearchOutlined />}
          onChange={e => {
            setSearch(e.target.value);
          }}
          style={{ marginBottom: 10 }}
          value={search}
        />
        <DevicesTable
          devices={addMode ? newGroupDevice : devices}
          onAdd={onAddDevice}
          onRemove={onRemove}
          addMode={addMode}
        />
      </div>
    </div>
  );
};

export default DeviceGroupDevicesTab;
